import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  FileText,
  Plus,
  Loader2,
  Sparkles,
  Edit,
  Trash2,
  Download,
  RefreshCw,
  Users,
  Briefcase,
  Handshake,
  BookOpen
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const TEMPLATE_TYPES = [
  { id: 'client_contract', name: 'Contrat Client', icon: Users, color: 'blue' },
  { id: 'creative_contract', name: 'Contrat Créatif', icon: Briefcase, color: 'purple' },
  { id: 'affiliate_contract', name: 'Contrat Apporteur', icon: Handshake, color: 'orange' },
  { id: 'commercial_brochure', name: 'Brochure Commerciale', icon: BookOpen, color: 'green' }
];

const TemplatesPage = () => {
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [context, setContext] = useState({});

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/templates/');
      setTemplates(res.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedType) return;
    setGenerating(true);
    try {
      const res = await api.post('/templates/generate', {
        template_type: selectedType,
        context: context
      });
      toast.success('Template généré avec succès !');
      setShowGenerateModal(false);
      setContext({});
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateBrochure = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/templates/brochure/generate-from-site');
      toast.success('Brochure générée depuis le site !');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      await api.put(`/templates/${selectedTemplate.id}`, { content: editContent });
      toast.success('Template mis à jour !');
      setShowEditModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      await api.delete(`/templates/${templateId}`);
      toast.success('Template supprimé');
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getTypeConfig = (type) => TEMPLATE_TYPES.find(t => t.id === type) || TEMPLATE_TYPES[0];

  const renderContextFields = () => {
    switch (selectedType) {
      case 'client_contract':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nom du client</Label><Input value={context.client_name || ''} onChange={e => setContext({...context, client_name: e.target.value})} /></div>
              <div><Label>Entreprise</Label><Input value={context.company_name || ''} onChange={e => setContext({...context, company_name: e.target.value})} /></div>
            </div>
            <div><Label>Description du projet</Label><Textarea value={context.project_description || ''} onChange={e => setContext({...context, project_description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Budget (€)</Label><Input type="number" value={context.budget || ''} onChange={e => setContext({...context, budget: e.target.value})} /></div>
              <div><Label>Délai</Label><Input value={context.deadline || ''} onChange={e => setContext({...context, deadline: e.target.value})} /></div>
            </div>
          </>
        );
      case 'creative_contract':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nom du créatif</Label><Input value={context.creative_name || ''} onChange={e => setContext({...context, creative_name: e.target.value})} /></div>
              <div><Label>Spécialités</Label><Input value={context.specialties || ''} onChange={e => setContext({...context, specialties: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tarif horaire (€)</Label><Input type="number" value={context.hourly_rate || ''} onChange={e => setContext({...context, hourly_rate: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={context.email || ''} onChange={e => setContext({...context, email: e.target.value})} /></div>
            </div>
          </>
        );
      case 'affiliate_contract':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nom de l'apporteur</Label><Input value={context.affiliate_name || ''} onChange={e => setContext({...context, affiliate_name: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={context.email || ''} onChange={e => setContext({...context, email: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Taux de commission (%)</Label><Input type="number" value={context.commission_rate || ''} onChange={e => setContext({...context, commission_rate: e.target.value})} /></div>
              <div><Label>Secteur</Label><Input value={context.sector || ''} onChange={e => setContext({...context, sector: e.target.value})} /></div>
            </div>
          </>
        );
      case 'commercial_brochure':
        return (
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-700">La brochure sera générée automatiquement à partir du contenu de votre site vitrine (services, portfolio, équipe).</p>
            <Button variant="outline" className="mt-3" onClick={handleGenerateBrochure} disabled={generating}>
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Générer depuis le site
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Modèles de documents</h1>
            <p className="text-sm text-slate-500">Générez et gérez vos contrats et brochures avec l'IA</p>
          </div>
          <Button onClick={() => setShowGenerateModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
            <Sparkles className="w-4 h-4 mr-2" />
            Nouveau template
          </Button>
        </div>

        {/* Template Types */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {TEMPLATE_TYPES.map(type => {
            const count = templates.filter(t => t.type === type.id).length;
            return (
              <Card key={type.id} className="card cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setSelectedType(type.id); setShowGenerateModal(true); }}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full bg-${type.color}-100 flex items-center justify-center`}>
                      <type.icon className={`w-5 h-5 text-${type.color}-600`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{type.name}</p>
                      <p className="text-xs text-slate-400">{count} template{count > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Templates List */}
        <Card className="card">
          <CardHeader>
            <CardTitle>Templates enregistrés</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Aucun template généré</p>
                <Button variant="link" onClick={() => setShowGenerateModal(true)}>Créer votre premier template</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map(template => {
                  const typeConfig = getTypeConfig(template.type);
                  return (
                    <div key={template.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <typeConfig.icon className={`w-5 h-5 text-${typeConfig.color}-600`} />
                        <div>
                          <p className="font-medium">{typeConfig.name}</p>
                          <p className="text-xs text-slate-400">Créé le {new Date(template.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedTemplate(template); setEditContent(template.content); setShowEditModal(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteTemplate(template.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Generate Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Générer un template avec l'IA</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type de document</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger><SelectValue placeholder="Choisir un type..." /></SelectTrigger>
                <SelectContent>
                  {TEMPLATE_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedType && renderContextFields()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateModal(false)}>Annuler</Button>
            {selectedType !== 'commercial_brochure' && (
              <Button onClick={handleGenerate} disabled={generating || !selectedType} style={{ backgroundColor: theme.colors?.accent }}>
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Générer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Modifier le template</DialogTitle>
          </DialogHeader>
          <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="min-h-[400px] font-mono text-sm" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
            <Button onClick={handleUpdateTemplate} style={{ backgroundColor: theme.colors?.accent }}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
